class SuperAdminApiService {
  // In sviluppo usa il backend diretto, in produzione usa il proxy nginx
  private baseURL = process.env.NODE_ENV === 'development' 
    ? 'http://localhost:3001/api/superadmin'
    : '/api/superadmin';
  
  private getHeaders() {
    const token = localStorage.getItem('superadmin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    const response = await fetch(url, config);
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json')
      ? await response.json()
      : await response.text();
    
    if (!response.ok) {
      if (response.status === 401) {
        // Token scaduto o non valido
        localStorage.removeItem('superadmin_token');
        localStorage.removeItem('superadmin_auth');
        window.location.href = '/superadmin/login';
      }
      const message =
        (typeof payload === 'object' && payload !== null && ('error' in payload || 'message' in payload))
          ? String((payload as { error?: string; message?: string }).error || (payload as { error?: string; message?: string }).message)
          : `HTTP error! status: ${response.status}`;
      throw new Error(message);
    }
    
    return payload;
  }

  // Autenticazione
  async login(username: string, password: string) {
    const response = await fetch(`${this.baseURL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      throw new Error(`Login failed: ${response.status}`);
    }

    return response.json();
  }

  // Dashboard e statistiche platform
  async getDashboard() {
    return this.request('/dashboard');
  }

  async getPlatformStats() {
    return this.request('/platform-stats');
  }

  // Statistiche Stripe
  async getStripeStats() {
    return this.request('/stripe/stats');
  }

  // Gestione tenant
  async getAllTenants() {
    return this.request('/tenants');
  }

  async getTenantDetails(tenantId: string) {
    return this.request(`/tenants/${tenantId}`);
  }

  async suspendTenant(tenantId: string) {
    return this.request(`/tenants/${tenantId}/suspend`, {
      method: 'POST',
    });
  }

  async activateTenant(tenantId: string) {
    return this.request(`/tenants/${tenantId}/activate`, {
      method: 'POST',
    });
  }

  async getTenantBackup(tenantId: string) {
    const response = await fetch(`${this.baseURL}/tenants/${tenantId}/backup`, {
      headers: this.getHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.blob();
  }

  async generateDemoData(tenantId: string) {
    return this.request(`/tenants/${tenantId}/generate-demo-data`, {
      method: 'POST',
    });
  }

  async clearDemoData(tenantId: string) {
    return this.request(`/tenants/${tenantId}/clear-demo-data`, {
      method: 'DELETE',
    });
  }

  async restoreTenantBackup(tenantId: string, backupFile: File) {
    const formData = new FormData();
    formData.append('backupFile', backupFile);
    
    return this.request(`/tenants/${tenantId}/restore`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': this.getHeaders().Authorization,
        // Non impostiamo Content-Type per FormData
      },
    });
  }

  // Analytics cross-tenant
  async getCrossTenantAnalytics(params?: {
    dateFrom?: string;
    dateTo?: string;
    metric?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
    }
    
    const endpoint = `/analytics/cross-tenant${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.request(endpoint);
  }

  // System health
  async getSystemHealth() {
    return this.request('/system/health');
  }

  async getDatabaseStats() {
    return this.request('/system/database-stats');
  }

  // Audit logs
  async getAuditLogs(params?: {
    limit?: number;
    offset?: number;
    action?: string;
    tenantId?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) queryParams.append(key, value.toString());
      });
    }
    
    const endpoint = `/audit-logs${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.request(endpoint);
  }

  // Email History
  async getEmailHistory(tenantId: string, params?: {
    limit?: number;
    offset?: number;
    stato?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) queryParams.append(key, value.toString());
      });
    }
    
    const endpoint = `/email-history/${tenantId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.request(endpoint);
  }

  async getEmailStats() {
    return this.request('/email-stats');
  }
}

export const superAdminApiService = new SuperAdminApiService();
